// Copyright (c) Docugami, Inc. All rights reserved.**/

import { Modal } from "@fluentui/react";
import { ChunkItemHeaderText, SmallButtonLabel } from "components/common/Fonts";
import { backgroundGray } from "components/shell/presentation/AppStyles";
import styled from "styled-components";
import { RichTextSanitizingHtmlViewer } from "./RichTextSanitizingHtmlViewer";
import { ExpandedRichTextCommand } from "hooks/ExpandedRichTextCommand";
import { ReportBindingEvaluationPreview } from "networking/reportsApi";
import { useBindingEvaluationData } from "hooks/bindingEvaluationData";
import { useEffect, useState } from "react";

const ModalContainer = styled.div`
    width: 100%;
    max-height: 60vh;
    box-sizing: border-box;
    overflow: auto;
`;

const ModalHeader = styled.div`
    display: flex;
    padding: 0px 5px;
    box-sizing: border-box;
    flex-direction: row;
    justify-content: space-between;
    overflow: hidden;
`;

const ModalHeaderText = styled(ChunkItemHeaderText)`
    margin: 0 auto;
    flex: 1;
    text-align: center;
    margin-right: 10px;
`;

const ModalContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
`

const ButtonContainer = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    background: ${backgroundGray};
    border-radius: 5px;
    padding: 0px 4px;
    margin-left: auto;
    height: 20px;
    justify-content: space-between;
    border-width: 0px;
    cursor: pointer;
    :hover {
        transform: scale(1.1);
    }
`;

const ButtonText = styled(SmallButtonLabel)`
    font-size: 11px;
    margin: 0px 5px;
`;

interface ExpandedRichTextModalProps {
    command: ExpandedRichTextCommand
}

export const ExpandedRichTextModal = (props: ExpandedRichTextModalProps) => {
    return (<Modal
        styles={{
            main: { maxWidth: "60%" },
            scrollableContent: { margin: "20px" }
        }}
        onDismiss={props.command.onDismiss}
        isOpen={!!props.command.isModalVisible}
        isDarkOverlay={false}
        isBlocking={false}>
        <ModalContentContainer>
            <ModalHeader>
                {!!props.command.label && <ModalHeaderText>{props.command.label}</ModalHeaderText>}
                <ButtonContainer onClick={props.command.onDismiss}>
                    <ButtonText>Close</ButtonText>
                </ButtonContainer>
            </ModalHeader>
            <ModalContainer>
                <RichTextSanitizingHtmlViewer html={props.command.richText} />
            </ModalContainer>
        </ModalContentContainer>
    </Modal>)
}

interface BindingEvaluationRichTextModalProps {
    evaluation?: ReportBindingEvaluationPreview,
    title?: string
    isOpen?: boolean;
    onDismiss?: () => void;
}

export const BindingEvaluationRichTextModal = (props: BindingEvaluationRichTextModalProps) => {
    const [, content] = useBindingEvaluationData(!!props.isOpen ? props.evaluation : undefined);
    const [cachedContent, setCachedContent] = useState<string | undefined>();
    
    // Avoid a UX hitch when isOpen => false making the content disappear while the modal is animating out
    useEffect(() => {
        if (!!props.isOpen) {
            setCachedContent(content);
        }
    }, [props.isOpen, content])

    return (<Modal
        styles={{
            main: { maxWidth: "60%" },
            scrollableContent: { margin: "20px" }
        }}
        onDismiss={props.onDismiss}
        isOpen={props.isOpen}
        isDarkOverlay={false}
        isBlocking={false}>
        <ModalContentContainer>
            <ModalHeader>
                {!!props.title && <ModalHeaderText>{props.title}</ModalHeaderText>}
                <ButtonContainer onClick={props.onDismiss}>
                    <ButtonText>Close</ButtonText>
                </ButtonContainer>
            </ModalHeader>
            <ModalContainer>
                <RichTextSanitizingHtmlViewer html={cachedContent ?? ''} />
            </ModalContainer>
        </ModalContentContainer>
    </Modal>)
}
